import { createContext, useContext, useState, ReactNode, FC } from "react";

// Types
export type RouteParams = Record<string, string>;

export type Route = {
  path: string;
  component: () => ReactNode;
};

type RouterContextType = {
  routes: Route[];
  currentRouteIndex: number;
  params: RouteParams;
  navigate: (path: string) => void;
};

const RouterContext = createContext<RouterContextType | null>(null);

const DefaultFallback = () => <div>404 - Page Not Found</div>;

const matchRoute = (
  routes: Route[],
  path: string
): { index: number; params: RouteParams } => {
  for (let i = 0; i < routes.length; i++) {
    const routeParts = routes[i].path.split("/");
    const pathParts = path.split("/");

    if (routeParts.length !== pathParts.length) continue;

    const params: RouteParams = {};
    let matches = true;

    for (let j = 0; j < routeParts.length; j++) {
      if (routeParts[j].startsWith(":")) {
        params[routeParts[j].slice(1)] = pathParts[j];
      } else if (routeParts[j] !== pathParts[j]) {
        matches = false;
        break;
      }
    }

    if (matches) return { index: i, params };
  }

  return { index: -1, params: {} };
};

export const RouterProvider: FC<{
  routes: Route[];
  children: ReactNode;
  initialPath?: string;
}> = ({ routes, children, initialPath = "/" }) => {
  const [currentRouteIndex, setCurrentRouteIndex] = useState(-1);
  const [params, setParams] = useState<RouteParams>({});

  const navigate = (path: string) => {
    const { index, params: newParams } = matchRoute(routes, path);
    setCurrentRouteIndex(index);
    setParams(newParams);
  };

  // Initial route match
  if (currentRouteIndex === -1) {
    const { index, params: initialParams } = matchRoute(routes, initialPath);
    setCurrentRouteIndex(index);
    setParams(initialParams);
  }

  return (
    <RouterContext.Provider
      value={{ routes, currentRouteIndex, params, navigate }}
    >
      {children}
    </RouterContext.Provider>
  );
};

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (!context) throw new Error("useRouter must be used within RouterProvider");
  return context;
};

export const useParams = () => useRouter().params;

export const CurrentRoute: FC<{ fallback?: () => ReactNode }> = ({
  fallback: Fallback = DefaultFallback,
}) => {
  const { routes, currentRouteIndex } = useRouter();
  if (currentRouteIndex === -1) return <Fallback />;

  const Component = routes[currentRouteIndex].component;
  return <Component />;
};

